import { Select, SelectContent, SelectItem } from '@dextech-ai/dex-design-system';
import { renderLabelOptions } from '@/utils/select-items';
import PropTypes from 'prop-types';

/**
 * Authoring UI for the attendance "Time In before Time Out" validation.
 *
 * Production shape (audit of admin_buildex_dev):
 *   [
 *     {
 *       type: "ARRAY" | "ROOT",
 *       arrayField?: string,           // required when type === "ARRAY"
 *       before: string,                // field name
 *       after:  string,                // field name
 *       operator: "lessThan" | string, // single observed value
 *     }
 *   ]
 *
 * UX: one card per rule, "+ Add Rule" appends. Copy is business-facing;
 * the saved schema still uses the existing runtime key names.
 */

const OPERATOR_OPTIONS = [
	{ value: 'lessThan', label: 'Time In must be before Time Out' },
	{ value: 'lessThanOrEqual', label: 'Time In can be same time or before Time Out' },
	{ value: 'greaterThan', label: 'First time must be after second time' },
	{ value: 'greaterThanOrEqual', label: 'First time can be same time or after second time' },
];

export default function DateCompareForm({
	value,
	onChange,
	disabled = false,
	fieldOptions = [],
}) {
	const rules = Array.isArray(value) ? value : [];

	const emit = (next) => {
		onChange(next.length > 0 ? next : null);
	};

	const update = (idx, patch) => {
		emit(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
	};

	const add = () => {
		emit([
			...rules,
			{ type: 'ROOT', before: '', after: '', operator: 'lessThan' },
		]);
	};

	const remove = (idx) => {
		emit(rules.filter((_, i) => i !== idx));
	};

	const fieldListId = 'cv-dc-field-options';

	return (
		<div className="dsv-cv-form" data-testid="cv-dc-form">
			<p className="dsv-cv-form__hint">
				Use this for attendance records. It checks that each Time In value
				is earlier than the matching Time Out value.
			</p>

			<datalist id={fieldListId}>
				{fieldOptions.map((opt) => <option key={opt} value={opt} />)}
			</datalist>

			{rules.length === 0 && (
				<p className="dsv-cv-form__empty" data-testid="cv-dc-empty">No date-compare rules yet.</p>
			)}

			{rules.map((rule, idx) => (
				<div key={idx} className="dsv-xallof-rule" data-testid="cv-dc-rule">
					<div className="dsv-modal__field-row">
						<div className="dsv-modal__field dsv-modal__field--half">
							<label className="dsv-modal__label">Where are the times?</label>
							<Select
								value={rule.type === 'ARRAY' ? 'ARRAY' : 'ROOT'}
								disabled={disabled}
								onChange={(next) => update(idx, { type: next })}
								data-testid="cv-dc-scope"
							>
								<SelectContent>
									<SelectItem value="ROOT">Single set of fields</SelectItem>
									<SelectItem value="ARRAY">Rows inside a list</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{rule.type === 'ARRAY' && (
							<div className="dsv-modal__field dsv-modal__field--half">
								<label className="dsv-modal__label">Attendance List Field</label>
								<input
									className="dsv-modal__input"
									type="text"
									list={fieldListId}
									value={rule.arrayField ?? ''}
									disabled={disabled}
									onChange={(e) => update(idx, { arrayField: e.target.value })}
									placeholder="e.g. person_attendance_details"
								/>
							</div>
						)}
					</div>

					<div className="dsv-modal__field-row">
						<div className="dsv-modal__field dsv-modal__field--third">
							<label className="dsv-modal__label">Time In Field</label>
							<input
								className="dsv-modal__input"
								type="text"
								list={fieldListId}
								value={rule.before ?? ''}
								disabled={disabled}
								onChange={(e) => update(idx, { before: e.target.value })}
								placeholder="e.g. time_in"
							/>
						</div>
						<div className="dsv-modal__field dsv-modal__field--third">
							<label className="dsv-modal__label">Time Out Field</label>
							<input
								className="dsv-modal__input"
								type="text"
								list={fieldListId}
								value={rule.after ?? ''}
								disabled={disabled}
								onChange={(e) => update(idx, { after: e.target.value })}
								placeholder="e.g. time_out"
							/>
						</div>
						<div className="dsv-modal__field dsv-modal__field--third">
							<label className="dsv-modal__label">Rule</label>
							<Select
								value={rule.operator || 'lessThan'}
								disabled={disabled}
								onChange={(next) => update(idx, { operator: next })}
								data-testid="cv-dc-operator"
							>
								<SelectContent>{renderLabelOptions(OPERATOR_OPTIONS)}</SelectContent>
							</Select>
						</div>
					</div>

					{!disabled && (
						<div className="dsv-modal__field-row">
							<button
								type="button"
								className="dsv-btn dsv-btn--secondary"
								onClick={() => remove(idx)}
								data-testid="cv-dc-remove"
							>
								Remove rule
							</button>
						</div>
					)}
				</div>
			))}

			{!disabled && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary"
					onClick={add}
					data-testid="cv-dc-add"
				>
					+ Add Rule
				</button>
			)}
		</div>
	);
}

DateCompareForm.propTypes = {
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	fieldOptions: PropTypes.arrayOf(PropTypes.string),
};
